import React from "react";
import {
  appointmentTimeLoud,
  durationLabel,
  linkLabel,
} from "../appointments";
import { agendaLabel } from "../meetingAgenda";
import type { ClientAppointment } from "../types/appointments.types";

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * How loudly the card is drawn.
 *
 * `quiet` is a fact being confirmed: the appointment a dialog is asking a
 * question *about*, set back so the question is what carries. `brand` is the
 * appointment being *changed* — the reschedule dialog's "currently scheduled",
 * which is the thing the three fields under it are a change to, and which the
 * design draws in the same teal as the fields themselves.
 */
export type AppointmentSummaryTone = "quiet" | "brand";

const TONES: Record<AppointmentSummaryTone, string> = {
  quiet: "border-transparent bg-[#F7F9FA]",
  brand: "border-[#BFEFF3] bg-[#F2FCFD]",
};

export interface AppointmentSummaryCardProps {
  appointment: ClientAppointment;
  tone?: AppointmentSummaryTone;
  /**
   * Drawn at the top right — the type pill the reschedule dialog wears.
   *
   * Passing one also takes "where it happens" off the line below. It is one
   * fact about the appointment and the card says it once: in the pill where
   * there is one, and on the line where there is not.
   */
  trailing?: React.ReactNode;
}

/**
 * The appointment a dialog is asking about, quoted inside it: the day as a
 * calendar leaf, what the session is for, the time and how long it runs.
 *
 * Both answers a past appointment can be given are irreversible in practice —
 * nothing on the screen unmarks a no-show — and both are raised from an icon
 * the width of a thumbnail in a table of near-identical rows. So the dialog
 * shows which row it came from rather than trusting the press.
 *
 * One card for the two dialogs that quote an appointment, rather than the two
 * strips they were drawn as. They were separate while the designs were: the
 * reschedule dialog quoted its appointment as a line of text. The design now
 * draws both as this card, and a second copy of it is how the two would start
 * disagreeing about what an appointment looks like when it is being quoted.
 *
 * The heading is what the session is for, where somebody chose one. "A
 * consultation on the 26th" is the sentence that tells two of a client's
 * appointments apart; "Appointment, 26th" is the sentence that does not, and
 * is only what this falls back to when the booking was made without an agenda.
 *
 * The link is not a link here. A dialog asking whether a call took place is
 * not the place to walk out of and into the call, and a blue-teal anchor
 * inside a confirmation is a way to lose the question by mis-clicking it.
 */
const AppointmentSummaryCard: React.FC<AppointmentSummaryCardProps> = ({
  appointment,
  tone = "quiet",
  trailing,
}) => {
  const start = new Date(appointment.startsAt);
  const agenda = agendaLabel(appointment.agenda);

  return (
    <div
      className={`flex items-center gap-4 rounded-[6px] border px-4 py-3 ${TONES[tone]}`}
    >
      {/* `aria-hidden` because the line beside it says the same day in words a
          screen reader can read out in one piece. */}
      <div
        aria-hidden="true"
        className="flex h-[54px] w-[54px] flex-shrink-0 flex-col items-center justify-center rounded-[6px] border border-[#BFEFF3] bg-white"
      >
        <span className="text-[11px] leading-none text-ink-muted">
          {MONTHS_SHORT[start.getMonth()]}
        </span>
        <span className="text-[20px] font-medium leading-tight text-ink">
          {start.getDate()}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-medium text-ink">
          {agenda || "Appointment"}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-ink-muted">
          {/* The time is the one thing on this line the reschedule dialog is
              about to change, so on that card it is the one thing coloured. */}
          <span className={tone === "brand" ? "text-[#00A5B4]" : undefined}>
            {appointmentTimeLoud(appointment.startsAt)}
          </span>
          <span aria-hidden="true" className="text-ink-subtle">
            •
          </span>
          <span>{durationLabel(appointment.durationMinutes)}</span>
          {/* Only where nothing is being drawn at the top right — see
              `trailing`. */}
          {!trailing && (
            <>
              <span aria-hidden="true" className="text-ink-subtle">
                •
              </span>
              {appointment.meetingUrl ? (
                <span className="break-all text-[#00A5B4]">
                  {linkLabel(appointment.meetingUrl)}
                </span>
              ) : (
                <span>{appointment.type}</span>
              )}
            </>
          )}
        </p>
      </div>

      {/* Against the top of the card rather than its middle, as the design
          draws it: it reads as a tag on the heading, not as a third column. */}
      {trailing && <div className="flex-shrink-0 self-start">{trailing}</div>}
    </div>
  );
};

export default AppointmentSummaryCard;
